import React, { useState } from 'react';
import {
  X,
  Search,
  Calendar,
  FileText,
  CreditCard,
  Mail,
  Phone,
  Zap,
  Clock,
  Tag,
  MessageSquare,
  AlertCircle,
  UserPlus,
  FileCheck,
  DollarSign,
  BarChart3,
  CheckCircle,
} from 'lucide-react';
import { Input } from '../ui/input';
import {
  filterTriggerCatalogGroups,
  getTriggerCatalogGroups,
} from '../../utils/workflowTriggerCatalog';
import { isPickerTriggerSelected } from '../../utils/triggerPickerUtils';
import TriggerPickerItem from './TriggerPickerItem';
import WorkflowTriggerDetailPanel from './WorkflowTriggerDetailPanel';
import WorkflowEditorPanelShell from './WorkflowEditorPanelShell';

// FundElite-specific trigger configurations
const AGENCY_TRIGGER_GROUPS = [
  {
    id: 'whop',
    name: 'Whop Integration',
    triggers: [
      { id: 'whop.membership_activated', name: 'Membership Activated', description: 'When a Whop membership becomes active', icon: CheckCircle, color: 'emerald' },
      { id: 'whop.payment_succeeded', name: 'Payment Succeeded', description: 'When a payment is successfully processed', icon: DollarSign, color: 'emerald' },
      { id: 'whop.payment_failed', name: 'Payment Failed', description: 'When a payment attempt fails', icon: AlertCircle, color: 'red' },
      { id: 'whop.invoice_past_due', name: 'Invoice Past Due', description: 'When an invoice becomes past due', icon: Clock, color: 'amber' },
      { id: 'whop.membership_cancel_at_period_end', name: 'Cancel At Period End', description: 'When membership is set to cancel at end of period', icon: Calendar, color: 'amber' },
      { id: 'whop.membership_deactivated', name: 'Membership Deactivated', description: 'When a membership is deactivated', icon: AlertCircle, color: 'red' },
      { id: 'whop.refund_created', name: 'Refund Created', description: 'When a refund is issued', icon: AlertCircle, color: 'amber' },
      { id: 'whop.dispute_created', name: 'Dispute Created', description: 'When a payment dispute is filed', icon: AlertCircle, color: 'red' },
      { id: 'whop.signup_invite_sent', name: 'Signup Invite Sent', description: 'When a Self Repair signup invite is created', icon: Mail, color: 'blue' },
      { id: 'whop.signup_invite_accepted', name: 'Signup Invite Accepted', description: 'When a client accepts the signup invite', icon: UserPlus, color: 'emerald' },
      { id: 'whop.unknown_purchase_intake', name: 'Unknown Purchase Intake', description: 'When an unmapped purchase is sent to intake', icon: AlertCircle, color: 'slate' },
    ],
  },
  {
    id: 'client',
    name: 'Client Lifecycle',
    triggers: [
      { id: 'client_created', name: 'Client Created', description: 'When a new client is added', icon: UserPlus, color: 'emerald' },
      { id: 'client_status_changed', name: 'Client Status Changed', description: 'When client status updates', icon: Zap, color: 'blue' },
      { id: 'client_tag_added', name: 'Tag Added to Client', description: 'When a tag is applied', icon: Tag, color: 'pink' },
      { id: 'client_tag_removed', name: 'Tag Removed from Client', description: 'When a tag is removed', icon: Tag, color: 'slate' },
    ],
  },
  {
    id: 'documents',
    name: 'Documents',
    triggers: [
      { id: 'document_requested', name: 'Document Requested', description: 'When documents are requested', icon: FileText, color: 'amber' },
      { id: 'document_uploaded', name: 'Document Uploaded', description: 'When client uploads document', icon: FileCheck, color: 'emerald' },
      { id: 'document_expired', name: 'Document Expired', description: 'When document passes expiry', icon: AlertCircle, color: 'red' },
    ],
  },
  {
    id: 'credit',
    name: 'Credit Repair',
    triggers: [
      { id: 'report_imported', name: 'Credit Report Imported', description: 'When report is imported', icon: BarChart3, color: 'teal' },
      { id: 'dispute_created', name: 'Dispute Created', description: 'When new dispute is filed', icon: FileText, color: 'purple' },
      { id: 'dispute_status_changed', name: 'Dispute Status Changed', description: 'When dispute status updates', icon: Zap, color: 'indigo' },
      { id: 'round_complete', name: 'Dispute Round Complete', description: 'When round of disputes completes', icon: Clock, color: 'cyan' },
    ],
  },
  {
    id: 'billing',
    name: 'Billing & Payments',
    triggers: [
      { id: 'invoice_created', name: 'Invoice Created', description: 'When invoice is generated', icon: CreditCard, color: 'blue' },
      { id: 'invoice_unpaid', name: 'Invoice Unpaid', description: 'When invoice becomes overdue', icon: AlertCircle, color: 'red' },
      { id: 'payment_received', name: 'Payment Received', description: 'When payment is processed', icon: DollarSign, color: 'emerald' },
      { id: 'payment_failed', name: 'Payment Failed', description: 'When payment attempt fails', icon: AlertCircle, color: 'red' },
    ],
  },
  {
    id: 'scheduling',
    name: 'Scheduling',
    triggers: [
      { id: 'appointment_scheduled', name: 'Appointment Scheduled', description: 'When appointment is booked', icon: Calendar, color: 'blue' },
      { id: 'appointment_cancelled', name: 'Appointment Cancelled', description: 'When appointment is cancelled', icon: Calendar, color: 'amber' },
      { id: 'consultation_missed', name: 'Consultation Missed', description: 'When client misses appointment', icon: AlertCircle, color: 'red' },
      { id: 'appointment_reminder', name: 'Appointment Reminder Due', description: 'Before scheduled appointment', icon: Clock, color: 'teal' },
    ],
  },
  {
    id: 'communication',
    name: 'Communication',
    triggers: [
      { id: 'email_opened', name: 'Email Opened', description: 'When client opens email', icon: Mail, color: 'blue' },
      { id: 'email_clicked', name: 'Email Link Clicked', description: 'When client clicks email link', icon: Mail, color: 'emerald' },
      { id: 'sms_received', name: 'SMS Received', description: 'When SMS reply is received', icon: MessageSquare, color: 'green' },
      { id: 'call_missed', name: 'Call Missed', description: 'When client call goes unanswered', icon: Phone, color: 'amber' },
    ],
  },
  {
    id: 'manual',
    name: 'Manual / Scheduled',
    triggers: [
      { id: 'manual_enroll', name: 'Manual Enrollment', description: 'Manually enroll contacts', icon: UserPlus, color: 'slate' },
      { id: 'scheduled_date', name: 'Scheduled Date', description: 'Run on specific dates', icon: Calendar, color: 'indigo' },
      { id: 'recurring_schedule', name: 'Recurring Schedule', description: 'Run on recurring schedule', icon: Clock, color: 'purple' },
    ],
  },
];

const TRIGGER_GROUPS = [
  ...getTriggerCatalogGroups(),
  ...AGENCY_TRIGGER_GROUPS,
];

const colorClasses = {
  blue: 'bg-blue-100 text-blue-600',
  green: 'bg-green-100 text-green-600',
  amber: 'bg-amber-100 text-amber-600',
  purple: 'bg-purple-100 text-purple-600',
  teal: 'bg-teal-100 text-teal-600',
  pink: 'bg-pink-100 text-pink-600',
  slate: 'bg-slate-100 text-slate-600',
  orange: 'bg-orange-100 text-orange-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  gray: 'bg-gray-100 text-gray-600',
  red: 'bg-red-100 text-red-600',
  emerald: 'bg-emerald-100 text-emerald-600',
};

const WorkflowTriggerPicker = ({
  isOpen,
  onClose,
  onSelectTrigger,
  activeTriggers = [],
  editingTriggerId = null,
  width,
  overlayMode = false,
  onOverlayDismiss,
  activePanelType = 'trigger-picker',
  chromeHeight,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [detailTrigger, setDetailTrigger] = useState(null);
  const [detailCategoryLabel, setDetailCategoryLabel] = useState('');

  if (!isOpen) return null;

  const filteredGroups = filterTriggerCatalogGroups(TRIGGER_GROUPS, searchQuery);

  const openTriggerDetail = (trigger, categoryLabel) => {
    setDetailTrigger(trigger);
    setDetailCategoryLabel(categoryLabel || '');
    setDetailPanelOpen(true);
  };

  const handleSaveTriggerDetail = (updatedTrigger) => {
    onSelectTrigger(updatedTrigger);
    setDetailPanelOpen(false);
    setDetailTrigger(null);
    setDetailCategoryLabel('');
  };

  if (detailPanelOpen && detailTrigger) {
    return (
      <WorkflowTriggerDetailPanel
        trigger={detailTrigger}
        categoryLabel={detailCategoryLabel}
        variant="light"
        width={width}
        overlayMode={overlayMode}
        onOverlayDismiss={onOverlayDismiss}
        activePanelType="trigger-detail"
        chromeHeight={chromeHeight}
        onBack={() => {
          setDetailPanelOpen(false);
          setDetailTrigger(null);
          setDetailCategoryLabel('');
        }}
        onClose={onClose}
        onSave={handleSaveTriggerDetail}
      />
    );
  }

  return (
    <WorkflowEditorPanelShell
      isOpen={isOpen}
      width={width ?? 384}
      overlayMode={overlayMode}
      chromeHeight={chromeHeight}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      className="border-l border-slate-200 bg-white"
      testId="trigger-picker"
    >
      <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-teal-500 to-teal-600">
        <div>
          <h3 className="font-semibold text-white">Select Trigger</h3>
          <p className="text-xs text-teal-100">Choose what starts this workflow</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      <div className="p-3 border-b border-slate-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            placeholder="Search triggers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredGroups.map((group) => (
          <div key={group.id} className="p-3 border-b border-slate-100 last:border-0">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 px-1">
              {group.name}
            </h4>
            <div className="space-y-1">
              {group.triggers.map((trigger) => (
                <TriggerPickerItem
                  key={trigger.id}
                  trigger={{
                    ...trigger,
                    eventKey: trigger.eventKey || trigger.id,
                  }}
                  categoryLabel={group.name}
                  variant="light"
                  icon={trigger.icon}
                  iconClassName={colorClasses[trigger.color]}
                  isSelected={isPickerTriggerSelected(trigger, activeTriggers, editingTriggerId)}
                  onOpen={(item) => openTriggerDetail(item, group.name)}
                />
              ))}
            </div>
          </div>
        ))}

        {filteredGroups.length === 0 && searchQuery && (
          <div className="p-8 text-center">
            <Search className="mx-auto text-slate-300 mb-3" size={32} />
            <p className="text-sm text-slate-500">No triggers found</p>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-slate-200 bg-slate-50">
        <p className="text-xs text-slate-500">
          <Zap size={12} className="inline mr-1" />
          Triggers define when a workflow starts. Each workflow can have one trigger.
        </p>
      </div>
      </div>
    </WorkflowEditorPanelShell>
  );
};

export { WorkflowTriggerPicker, TRIGGER_GROUPS };
export default WorkflowTriggerPicker;
